import { Component, OnInit, OnDestroy, OnChanges, Input, Output, EventEmitter, ElementRef, Renderer } from '@angular/core';

// Services
import { CatalogService } from '../../services/catalog-service';
import { UtilsService } from '../../../../shared/services/utils-service';
import { AnimateService } from '../../../../shared/services/animate-service';

let debug, debugOff = function (...any) { };
process.env.NODE_ENV !== 'production' && (debug = require('debug')('app:ProductCategoriesCmp'));

/**
 * Renders the categories views.
 * Standard angular component, no direct access to state store.
 * All categories are derived from a root category.
 */
@Component({
    selector: 'product-categories-cmp',
    template: require('./product-categories.cmp.html'),
    styles: [String(require('./product-categories.cmp.scss'))],
    host: { class: 'product-categories' }
})
export class ProductCategoriesCmp {

    @Input() catalogState;
    @Input() scrollContainerElem; // Usually, scroll container is located one component above
    @Output() categorySelected = new EventEmitter();

    private _prevCategory = null;
    private _currTopCategory = null;
    private _categoriesElems = null;
    private _rootSubcategs = null;
    private _rootCategory = null;
    private _renderCategory = null;
    private _subscriptions: any = {};
    private _timeouts: any = {};
    private _listeners: any = {};
    private _prodCategsElem = null;
    private _mutationObserver = null;
    private _currentCategoryId = null;
    private _domIsRendered: boolean = false;
    private _selectCategByScrollIsEnabled: boolean = true;
    private _scrollToSelectedCategIsEnabled: boolean = true;


    constructor(
        private _elementRef: ElementRef,
        private _catalogService: CatalogService,
        private _utilsService: UtilsService,
        private _renderer: Renderer,
        private _animateService: AnimateService
    ) {
        //'Construct product categories component');
        this._getProductCategories();
    }


    ngOnInit() {
        //'Initialise product categories component');

        let self = this;

        // Cache categories dom elements
        this._cacheDomElements();

        // When dom rendering is finished
        this._onCategoriesRendered(() => {

            // Cache top categories dom elements
            this._cacheTopCategsElems();

            // Run once, just onInit
            if (this._domIsRendered === true) { return; }

            // Detect rendering
            this._domIsRendered === false && (this._domIsRendered = true);

            // Restore categories scroll position
            // Delay necessary to ensure that all children components are rendered. Mutation observer is not enough.
            self.catalogState.renderMode === 'expanded' &&
                setTimeout(() => {
                    self._scrollToCategory(self._currentCategoryId, 800);
                }, 400);
        });

        // Listen for scroll, only when catalog rendering mode is `expanded`
        this._listeners.catalogScroll = this._renderer.listen(this.scrollContainerElem, 'scroll',
            event => self.catalogState.renderMode !== 'compact' && self._selectTopCategoryByScroll(event)
        );

        // Subscribe to category selected
        this._catalogService.events.categorySelected.subscribe((event)=>{
            //`==>Category "${event.category.id}" selected:`, [event.category]);

            // Wait for angular to render the category
            setTimeout(()=>{

                // Scroll to category
                this._scrollToCategory(event.category.id, 300);

            }, 0);
            
        });

    }


    ngOnChanges(changes) {
        //'Product categories input changes detected:', [changes]);

        // Detect current category change from state store
        let currentCategoryId = this._catalogService.checkCurrentCategoryChanged(changes);
        currentCategoryId !== false && this._handleCurrentCategChanged(currentCategoryId);

        // Detect catalog render mode change from state store
        let renderMode = this._catalogService.checkRenderModeChanged(changes);
        renderMode !== false && this._handleCatalogRenderModeChanged(renderMode);
    }


    ngOnDestroy() {
        //'Destroy product categories component');

        // Unsubscribe events
        for (let subId in this._subscriptions) {
            this._subscriptions[subId].unsubscribe();
        }

        // Disconnect mutation observer
        this._mutationObserver.disconnect();

        // Remove scroll listener
        this._listeners.catalogScroll();
    }


    private _handleCurrentCategChanged(currentCategoryId) {
        debugOff('Handle current category changed');

        // Cache current category id
        this._currentCategoryId = currentCategoryId;

        // Execute only after host component dom was rendered
        if (this._domIsRendered === true) {

            if (this.catalogState.renderMode === 'expanded') {

                // Update categories scroll position
                this._scrollToCategory(this._currentCategoryId, 800);

            } else if (this.catalogState.renderMode === 'compact') {

                // Reset scroll
                this.scrollContainerElem.scrollTop = 0;

                // Render selected category
                this._renderSelectedCategory(currentCategoryId);

            }

        }

        // Cache current selected category
        this._currTopCategory = this._catalogService.getProductCategoryById(this.catalogState.currentCategoryId);
        debugOff('Current top Category:', this._currTopCategory);
    }


    private _handleCatalogRenderModeChanged(renderMode) {
        debugOff('Handle render mode changed');
        debugOff('Render mode:', renderMode);

        this._renderCategory = null;

        // Reset root category to original if catalog render mode is expanded
        // renderMode === 'expanded' && (this._renderCategory = this._rootCategory);
        this._renderCategory = this._rootCategory
    }

    // TODO: Finish implementation, test on mobile

    private _renderSelectedCategory(currentCategoryId) {
        //'Render selected category "' + currentCategoryId + '"');

        // Shallow clone selected category
        let selectedCategory = this._catalogService.getProductCategoryById(currentCategoryId);
        // let rootCategory = this._catalogService.shallowCloneCategory( selectedCategory );

        // Update root category
        // rootCategory.id = 'rootCategory';
        // this._verbose &&
        // // '→ Updated root category:', rootCategory );
        //'→ Updated render category:', selectedCategory);

        // this._renderCategory = rootCategory;
        // this._renderCategory = selectedCategory;
    }


    private _cacheDomElements() {
        //'Cache DOM elements');

        this._prodCategsElem = this._elementRef.nativeElement;
        debugOff('Product categories list element:', this._prodCategsElem);
    }

    /** Retrieve categories on initialisation and at run time, for example, when switching routes.
     */
    private _getProductCategories() {
        //'Get product categories');

        let catalogDataIsLoaded = this._catalogService.checkCatalogDataIsLoaded();

        // Get catalog data either from cache or via subscription
        if (catalogDataIsLoaded === true) {
            debugOff('Get catalog cached product categories');
            this._rootCategory = this._catalogService.getRootCategory();
            this._renderCategory = this._catalogService.getRootCategory();
            debugOff('Root category:', this._rootCategory);
        } else {
            debugOff('Subscribe for product categories');
            this._subscriptions.catalogDataLoadedAsync = this._catalogService.events.catalogDataLoadedAsync.subscribe(
                event => {
                    debugOff('Subscription for product categories response');
                    //'Get product categories');
                    this._rootCategory = event.rootCategory;
                    this._renderCategory = event.rootCategory;
                    debugOff('Root category async:', this._rootCategory);
                }
            );
        }
    }

    /**
     * Scrolls page to category or subcategory. 
     * Check places of invocations to understand when to use it.
     */
    private _scrollToCategory(categId, duration) {

        if (this._scrollToSelectedCategIsEnabled === false) { return; }

        // categId = 'spa-therapies'

        //'==>Scroll to "' + categId + '"');

        let self = this,
            startPosition,
            endPosition,
            targetElement,
            targetElemWindowOffset,
            scrollDistance;

        startPosition = this.scrollContainerElem.scrollTop;
        debugOff('Starting position:', startPosition);

        targetElement = document.querySelector('#' + categId + '-product-category');
        debugOff('Target element:', targetElement);

        targetElemWindowOffset = this._utilsService.getElementToWindowOffset(targetElement);
        debugOff('Target element offset:', targetElemWindowOffset);

        scrollDistance = targetElemWindowOffset.y;
        debugOff('Scroll distance:', scrollDistance);

        endPosition = startPosition + scrollDistance;
        debugOff('End position:', endPosition);

        // Slide view to selected category
        this._animateService.start(startPosition, endPosition, duration, 'easeInOutQuad', function (curr, count, max) {
            self.scrollContainerElem.scrollTop = curr;

            // Prevent triggering of _selectTopCategoryByScroll() by the scroll animation
            self._selectCategByScrollIsEnabled = false;
            count >= max && setTimeout(() => self._selectCategByScrollIsEnabled = true, 50);
        }, this._timeouts, '_scrollToCategory');

    }


    private _cacheTopCategsElems() {
        //'Cache top categories DOM elements');

        this._rootSubcategs = this._catalogService.getRootCategory().subcategories;
        debugOff('Root subcategories:', this._rootSubcategs);

        // Create cache object
        this._categoriesElems = {};

        // Cache dom elements
        this._rootSubcategs.forEach(category => {
            this._categoriesElems[category.id] = document.querySelector('#' + category.id + '-product-category');
        });

        debugOff('Cached subcategories elements:', this._categoriesElems);
    }


    private _selectTopCategoryByScroll(event) {
        debugOff('Select top category by scroll');
        debugOff('Event:', event);

        if (this._selectCategByScrollIsEnabled === false) { return; }

        let offset = -200,
            self = this;

        this._prevCategory = this._currTopCategory;

        // Measure scroll offset in order to detect current category
        this._rootSubcategs.forEach(category => {

            // Advanced debug
            // debugOff( 'Category:', category );
            // debugOff( 'Category DOM element:', self._categoriesElems[category.id] );

            if (self._categoriesElems[category.id] !== null &&
                event.srcElement.scrollTop > self._categoriesElems[category.id].offsetTop + offset
            ) {
                self._currTopCategory = category;

                // Advanced debug
                // debugOff( 'Current top category:', self._currTopCategory );
            }
        });

        // Select category
        if (this._prevCategory.id !== this._currTopCategory.id) {
            //'Selected "' + this._currTopCategory + '" category:');
            debugOff('Selected category:', this._currTopCategory);

            // Prevent scroll to category animation
            this._scrollToSelectedCategIsEnabled = false;
            let self = this;

            // Clear previous timeout
            clearTimeout(this._timeouts.blockScrollToAnimation);

            // Remove restriction for scroll to category animation
            this._timeouts.blockScrollToAnimation = setTimeout(() => {
                self._scrollToSelectedCategIsEnabled = true;
            }, 100);

            // Emit category change event
            this.categorySelected.emit({
                category: self._currTopCategory
            });

        }
    }

    /** Detects if dom elements have been rendered after ngFor.
     * This step is necessary in order to query the DOM for the newly created elements.
     */
    private _onCategoriesRendered(callback) {
        //'Detect when product categories dom is rendered');
        this._mutationObserver = new MutationObserver(() => {
            //'Product categories dom is rendered');
            callback();
        });
        this._mutationObserver.observe(this._prodCategsElem, { childList: true, subtree: true });
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./app/catalog/shared/components/product-categories/product-categories.cmp.ts